import { getEnvironment } from '@/shared/config/environment';
import { getSentry } from '@/shared/lib/sentry';

/**
 * Centralized logger utility with environment-specific behavior
 * - Development: Full console output, no Sentry
 * - Staging: Console output + Sentry error tracking
 * - Production: No console output, only Sentry error tracking
 *
 * Features:
 * - Lazy evaluation for performance (use function arguments when needed)
 * - Sampling for high-frequency logs
 * - Environment-aware logging
 */

// Sampling rate tracking for high-frequency logs
const samplingState = new Map<string, { count: number; lastLogged: number }>();

/**
 * Sample logs to reduce noise for high-frequency events
 * @param key Unique identifier for the log type
 * @param sampleRate Log every Nth occurrence (default: 10)
 */
const shouldSample = (key: string, sampleRate = 10): boolean => {
  const state = samplingState.get(key) || { count: 0, lastLogged: 0 };
  state.count++;

  if (state.count - state.lastLogged >= sampleRate) {
    state.lastLogged = state.count;
    samplingState.set(key, state);
    return true;
  }

  samplingState.set(key, state);
  return false;
};

export const logger = {
  /**
   * General logging - only visible in development
   */
  log: (...args: unknown[]) => {
    const env = getEnvironment();
    if (env === 'development') {
      console.log(...args);
    }
  },

  /**
   * Error logging - always tracked in staging/production
   */
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    const env = getEnvironment();
    const isProduction = env === 'production';
    const isStaging = env === 'staging';

    // Always log to console in dev/staging for debugging
    if (!isProduction) {
      console.error(message, error, context);
    }

    // Send to Sentry in staging/production
    const Sentry = getSentry();
    if (Sentry && (isStaging || isProduction) && error) {
      // Normalize non-Error values (e.g., Auth0 throws plain objects like { error: 'login_required' })
      // so Sentry can display a useful title instead of "Object captured as exception with keys: error"
      const normalizedError = error instanceof Error
        ? error
        : new Error(typeof error === 'object' ? JSON.stringify(error) : String(error));

      Sentry.captureException(normalizedError, {
        extra: {
          ...context,
          message,
          originalError: error,
          environment: env
        },
        tags: {
          environment: env
        }
      });
    } else if (Sentry && (isStaging || isProduction) && !error) {
      // If no error object, capture message
      Sentry.captureMessage(message, 'error');
    }
  },

  /**
   * Warning logging - visible in development and staging
   */
  warn: (...args: unknown[]) => {
    const env = getEnvironment();
    if (env !== 'production') {
      console.warn(...args);
    }
  },

  /**
   * Info logging - only visible in development
   */
  info: (...args: unknown[]) => {
    const env = getEnvironment();
    if (env === 'development') {
      console.info(...args);
    }
  },

  /**
   * Debug logging - only visible in development
   */
  debug: (...args: unknown[]) => {
    const env = getEnvironment();
    if (env === 'development') {
      console.debug(...args);
    }
  },

  /**
   * Sampled debug logging for high-frequency events
   * Use this for timer progress, frequent updates, etc.
   * @param key Unique identifier for the log type (e.g., 'timer:progress')
   * @param sampleRate Log every Nth occurrence (default: 10)
   */
  debugSampled: (key: string, sampleRate = 10, ...args: unknown[]) => {
    const env = getEnvironment();
    if (env === 'development' && shouldSample(key, sampleRate)) {
      console.debug(
        `[Sampled ${sampleRate}x]`,
        `[${samplingState.get(key)?.count}]`,
        ...args,
      );
    }
  },

  /**
   * Track custom events or user actions
   */
  trackEvent: (eventName: string, data?: Record<string, unknown>) => {
    const env = getEnvironment();
    const isStaging = env === 'staging';
    const isProduction = env === 'production';
    const isDevelopment = env === 'development';

    const Sentry = getSentry();
    if (Sentry && (isStaging || isProduction)) {
      Sentry.addBreadcrumb({
        message: eventName,
        category: 'user-action',
        level: 'info',
        data
      });
    }

    if (isDevelopment) {
      console.log(`[Event] ${eventName}`, data);
    }
  },

  /**
   * Performance timing utility
   * Use with logger.timeEnd to measure execution time
   */
  time: (label: string) => {
    const env = getEnvironment();
    if (env === 'development') {
      console.time(label);
    }
  },

  /**
   * End performance timing
   */
  timeEnd: (label: string) => {
    const env = getEnvironment();
    if (env === 'development') {
      console.timeEnd(label);
    }
  },
};
